import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import EditDocumentIcon from "@mui/icons-material/EditDocument";
import { Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useUserData } from "../../../../auth/useUserData.js";
import { SaveTemplateDialog } from "../SaveTemplateDialog.js";
import {
  useQuoteTemplateUpdateFromItemMenuItem_ParentItemQuery,
  useQuoteTemplateUpdateFromItemMenuItem_TemplateQuery,
} from "./QuoteTemplateUpdateFromItemMenuItem.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  /** The item the ⋯ menu belongs to; its content is what the template receives. */
  itemId: string;
  /** `null` for the quote's root item. */
  parentItemId: string | null;
  /** Non-null — the caller renders this entry only for a template-sourced item. */
  templateOriginQuoteId: string;
  sourceTitle: string;
  refetchQueries?: string[];
}

/**
 * The ⋯ menu's update entry for a section that came from a template.
 *
 * Shown on the sub-tree root only: every cloned item carries
 * `templateOriginQuoteId`, so without comparing against the parent a child would
 * offer to overwrite the whole template with its own fragment.
 *
 * Both lookups are lazy by construction — this component mounts with the open
 * menu — because the label depends on the referenced template's title and
 * ownership, which the tree query has no reason to carry.
 *
 * This is an additional entry; the menu's existing "Save as quote template"
 * stays as it is, so a subscribed source keeps a plain save path alongside.
 */
export const QuoteTemplateUpdateFromItemMenuItem = ({
  projectId,
  docId,
  itemId,
  parentItemId,
  templateOriginQuoteId,
  sourceTitle,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["Templates", "QuoteItem"]);
  const client = useApolloClient();
  const viewer = useUserData()?.currentUser;

  const parentQuery = useQuoteTemplateUpdateFromItemMenuItem_ParentItemQuery({
    client,
    variables: { projectId, docId, parentItemId: parentItemId ?? "" },
    skip: !parentItemId,
  });
  const parentItem =
    parentQuery.data?.item.__typename === "Item"
      ? parentQuery.data.item
      : undefined;

  const templateQuery = useQuoteTemplateUpdateFromItemMenuItem_TemplateQuery({
    client,
    variables: { id: templateOriginQuoteId },
    // The menu opens on its own; the user never asked for this lookup, so a
    // failure must stay silent rather than raise the error link's toast.
    context: { noErrorNotification: true },
  });
  const template = getDataOrNull(
    templateQuery.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate;

  // An item with no parent is the quote's root — a sub-tree root by definition.
  const isSubTreeRoot =
    !parentItemId ||
    (!!parentQuery.data &&
      parentItem?.templateOriginQuoteId !== templateOriginQuoteId);

  const isLoading =
    (!!parentItemId && parentQuery.loading) || templateQuery.loading;

  // An entry whose label could not be resolved would lie. A template the
  // organisation merely does not own resolves fine and forks to "Save as new
  // template" below.
  if (parentQuery.error || templateQuery.error) return null;

  if (isLoading)
    // Neutral while in flight: the real label depends on ownership, so anything
    // more specific would flicker into a different action once data lands.
    return (
      <MenuItemWithIcon icon={<EditDocumentIcon />} disabled>
        {t("Update template…", { ns: "Templates" })}
      </MenuItemWithIcon>
    );

  // The protected lookup reaches the same dead end without raising an error: a
  // `MissingPermissions`/`MissingCapabilities` result carries data but no
  // `quoteTemplate`, so the guard above never sees it. Settled and still
  // empty — deleted, or a viewer without template_document_read — leaves nothing
  // to name and nothing to update, and the menu's own "Save as quote template"
  // right above already offers the copy.
  if (!template) return null;

  if (!isSubTreeRoot) return null;

  // A template the organisation does not own cannot be overwritten, so it offers
  // a copy instead.
  if (template.owningSystemOrganisationId !== viewer?.organisation?.id)
    return (
      <ModalOpenButton
        Modal={SaveTemplateDialog}
        modalProps={{
          projectId,
          docId,
          sourceItemId: itemId,
          originItemId: itemId,
          sourceTitle,
          mode: { type: "create" as const },
          refetchQueries,
        }}
      >
        <MenuItemWithIcon icon={<LibraryAddIcon />}>
          <Stack direction="column">
            <Typography variant="body2">
              {t("Save as new template", { ns: "Templates" })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(
                "A subscribed template can't be overwritten. The copy won't receive upstream updates.",
                { ns: "Templates" }
              )}
            </Typography>
          </Stack>
        </MenuItemWithIcon>
      </ModalOpenButton>
    );

  const updateLabel = t('Update template "{name}"', {
    ns: "Templates",
    name: template.title,
  });

  return (
    <ModalOpenButton
      Modal={SaveTemplateDialog}
      modalProps={{
        projectId,
        docId,
        sourceItemId: itemId,
        originItemId: itemId,
        sourceTitle,
        mode: { type: "update" as const, templateDocId: template.id },
        refetchQueries,
      }}
    >
      <MenuItemWithIcon icon={<EditDocumentIcon />}>
        {/* A template title has no length limit and the label embeds it, so
            left alone one long name stretches the whole ⋯ menu across the
            screen. Clipped visually, not shortened: the full title stays in the
            DOM for screen readers and comes back on hover. */}
        <Tooltip title={updateLabel}>
          <Typography variant="inherit" noWrap sx={{ maxWidth: 320 }}>
            {updateLabel}
          </Typography>
        </Tooltip>
      </MenuItemWithIcon>
    </ModalOpenButton>
  );
};
